import { isGoogleSheetTool } from '@/modules/workflows/components/sop-editor/components/google-sheet-utils';
import {
  ASSIGN_TARGET_LABELS,
  BRAND_DEFAULT_LABEL,
  assignTargetOf,
  unassignTargetOf,
  type AssignTarget,
  type UnassignTarget
} from '@/modules/escalation-policy/escalation-policy';
import type { JSONContent } from '@tiptap/react';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import {
  ACTION_CATALOG,
  KIM_ACTION_PREFIX,
  KIM_PROVIDER,
  POST_PROCESSING_ACTIONS,
  SUB_PROTOCOL_ACTION_ID,
  actionMeta,
  type ProtocolActionId,
  type TicketActionId
} from '../constants';
import {
  FOLLOWUP_WAIT_PROVIDER,
  FOLLOWUP_WAIT_TOOL,
  defaultWaitProgram,
  readWaitProgram,
  waitChipLabel,
  type WaitProgram
} from './wait-program';

/** A Google Sheets chip's tool and chosen sheet: what the sheet picker reads and writes. */
export interface ProtocolSheetRef {
  tool: string;
  sheetUrl: string | null;
  tabName: string | null;
}

// Every chip is an actionChip, an escalateChip or a workflowLinkChip.
export type ProtocolChip =
  | { kind: 'tool'; actionId: string; label: string; provider: string; sheet?: ProtocolSheetRef }
  | {
      kind: 'assign';
      assignTarget: AssignTarget;
      agentId: string;
      agentName: string;
      agentEmail: string;
      agentProvider: string;
      sentinelUserId: string;
      sentinelUserName: string;
      sentinelUserEmail: string;
    }
  | { kind: 'unassign'; assignTarget: UnassignTarget }
  | { kind: 'email'; to: string; cc: string; bcc: string; subject: string; body: string }
  | { kind: 'note'; noteContent: string }
  | { kind: 'slack'; slackChannel: string; slackMessage: string }
  | { kind: 'action'; action: TicketActionId; label: string }
  | { kind: 'subProtocol'; subProtocolId: string | null; label: string }
  | { kind: 'wait'; label: string; program: WaitProgram }
  | { kind: 'workflowLink'; workflowId: string | null; label: string };

export interface ToolChipInput {
  actionId: string;
  label: string;
  provider: string;
  tool?: string;
  description?: string;
  inputSchema?: object;
  outputSchema?: object;
  riskLevel?: string;
}

const attrsOf = (node: JSONContent): Record<string, unknown> =>
  (node.attrs as Record<string, unknown> | undefined) ?? {};

const str = (value: unknown): string => (typeof value === 'string' ? value : '');

export const toolChipNode = (tool: ToolChipInput): JSONContent => ({
  type: 'actionChip',
  attrs: {
    actionId: tool.actionId,
    label: tool.label,
    provider: tool.provider,
    color: tool.provider.toLowerCase() === 'shopify' ? 'green' : 'blue',
    tool: tool.tool ?? tool.actionId,
    description: tool.description,
    inputSchema: tool.inputSchema,
    outputSchema: tool.outputSchema,
    riskLevel: tool.riskLevel
  }
});

/** The status values a tool accepts, from its input schema; undefined when it lists none. */
const statusEnum = (tool: ToolDefinitionAction): unknown[] | undefined => {
  const status = (tool.inputSchema as { properties?: { status?: { enum?: unknown } } } | undefined)?.properties?.status;
  return Array.isArray(status?.enum) ? status.enum : undefined;
};

/**
 * The account's post-processing tool behind a ticket action, if its helpdesk has one. A status
 * action also needs the helpdesk to have that status: the tool lists only the states its helpdesk
 * really has (Gorgias has no pending or snoozed), so an action it cannot do is never offered.
 */
export const postProcessingTool = (action: TicketActionId, tools: ToolDefinitionAction[]) => {
  const spec = POST_PROCESSING_ACTIONS[action];
  if (!spec) return undefined;
  const status = spec.staticInputs?.['status'];
  return tools.find(
    (tool) => tool.postProcessing && tool.tool === spec.tool && (status === undefined || (statusEnum(tool)?.includes(status) ?? true))
  );
};

/** The platform's followup_wait tool, when the account has it. */
export const followupWaitTool = (tools: ToolDefinitionAction[]): ToolDefinitionAction | undefined =>
  tools.find((tool) => tool.tool === FOLLOWUP_WAIT_TOOL && tool.postProcessing);

/** A Wait block: post-processing chip whose staticInputs hold the wait program. */
export const waitChipNode = (tools: ToolDefinitionAction[] = []): JSONContent => {
  const tool = followupWaitTool(tools);
  const program = defaultWaitProgram();
  const label = waitChipLabel(program);
  return {
    type: 'actionChip',
    attrs: {
      actionId: tool?.id ?? FOLLOWUP_WAIT_TOOL,
      label,
      provider: tool?.provider ?? FOLLOWUP_WAIT_PROVIDER,
      color: tool?.color ?? 'blue',
      tool: tool?.tool ?? FOLLOWUP_WAIT_TOOL,
      description: tool?.description ?? 'Wait, then follow up or act on the ticket',
      inputSchema: tool?.inputSchema ?? {},
      outputSchema: tool?.outputSchema ?? {},
      riskLevel: tool?.riskLevel ?? 'low',
      postProcessing: true,
      staticInputs: { program }
    }
  };
};

// A ticket action is a post-processing tool call when the helpdesk has the tool; the plain kim- chip otherwise.
export const actionChipNode = (
  action: TicketActionId,
  label: string,
  tools: ToolDefinitionAction[] = [],
  /** An option the author picked for this action: a snooze preset, a forced close. */
  option?: Record<string, unknown>
): JSONContent => {
  const tool = postProcessingTool(action, tools);
  if (!tool) {
    return {
      type: 'actionChip',
      attrs: { actionId: `${KIM_ACTION_PREFIX}${action}`, label, provider: KIM_PROVIDER, color: 'blue' }
    };
  }
  // Set Ticket Fields starts empty: the author picks the values once the chip is placed.
  const fixed =
    action === 'tag' ? { tags: [label] } : action === 'attributes' ? { fields: [] } : POST_PROCESSING_ACTIONS[action]?.staticInputs;
  const staticInputs = fixed || option ? { ...fixed, ...option } : null;
  return {
    type: 'actionChip',
    attrs: {
      actionId: tool.id,
      label,
      provider: tool.provider,
      color: tool.color,
      tool: tool.tool,
      description: tool.description,
      inputSchema: tool.inputSchema,
      outputSchema: tool.outputSchema,
      riskLevel: tool.riskLevel,
      postProcessing: true,
      staticInputs
    }
  };
};

/** Which ticket action a post-processing chip is, read back off its tool and fixed values. */
function postProcessingAction(attrs: Record<string, unknown>): TicketActionId | null {
  const statics = (attrs['staticInputs'] as Record<string, unknown> | null) ?? {};
  const entry = Object.entries(POST_PROCESSING_ACTIONS).find(
    ([, spec]) =>
      spec.tool === attrs['tool'] &&
      Object.entries(spec.staticInputs ?? {}).every(([key, value]) => statics[key] === value)
  );
  return (entry?.[0] as TicketActionId | undefined) ?? null;
}

export const subProtocolChipNode = (subProtocolId: string | null, name: string): JSONContent => ({
  type: 'actionChip',
  attrs: {
    actionId: SUB_PROTOCOL_ACTION_ID,
    label: name,
    provider: KIM_PROVIDER,
    color: 'blue',
    subProtocolId
  }
});

/** Link Protocol: its own node type, so the compiler never mistakes it for a tool call. */
export const workflowLinkChipNode = (workflowId: string, workflowName: string): JSONContent => ({
  type: 'workflowLinkChip',
  attrs: { workflowId, workflowName }
});

/** An agent of the brand's external helpdesk, the one an Assign to Agent assigns the ticket to. */
export interface HelpdeskAgentRef {
  id: string;
  name: string;
  email?: string;
  provider?: string;
}

/** A Kim user with the Sentinel role, the one an Assign to Sentinel makes the ticket's owner. */
export interface SentinelUserRef {
  /** The user's numeric id, as a string. */
  id: string;
  name: string;
  email?: string;
}

const NO_AGENT = { agentId: '', agentName: '', agentEmail: '', agentProvider: '' };
const NO_SENTINEL = { sentinelUserId: '', sentinelUserName: '', sentinelUserEmail: '' };

/** Assign to Agent; no agent leaves the choice to the brand's escalation policy. */
export const assignAgentChipNode = (agent?: HelpdeskAgentRef): JSONContent => ({
  type: 'escalateChip',
  attrs: {
    escalationType: 'assign',
    assignTarget: 'agent',
    agentId: agent?.id ?? '',
    agentName: agent?.name ?? '',
    agentEmail: agent?.email ?? '',
    agentProvider: agent?.provider ?? '',
    ...NO_SENTINEL
  }
});

/** Assign to Sentinel; no Sentinel leaves the choice to the brand's escalation policy. */
export const assignSentinelChipNode = (sentinel?: SentinelUserRef): JSONContent => ({
  type: 'escalateChip',
  attrs: {
    escalationType: 'assign',
    assignTarget: 'sentinel',
    ...NO_AGENT,
    sentinelUserId: sentinel?.id ?? '',
    sentinelUserName: sentinel?.name ?? '',
    sentinelUserEmail: sentinel?.email ?? ''
  }
});

/** Unassign: clears the helpdesk ticket's agent, or the Sentinel owner; a blank target is the brand's default. */
export const unassignChipNode = (target: UnassignTarget): JSONContent => ({
  type: 'escalateChip',
  attrs: { escalationType: 'unassign', assignTarget: target }
});

/**
 * Rewrites a chip saved before Assign had targets: the legacy Assign to User (`assign-to-human`)
 * is an Assign to Agent, and an Assign with no target acts on the agent. An Unassign with no
 * target is left blank: the brand's escalation policy picks it. Anything else comes back as it was.
 */
export function upgradeLegacyChip(node: JSONContent): JSONContent {
  if (node.type !== 'escalateChip') return node;
  const attrs = attrsOf(node);
  const type = attrs['escalationType'];
  if (type === 'assign-to-human') return { ...node, attrs: { ...attrs, escalationType: 'assign', assignTarget: 'agent' } };
  if (type === 'assign' && attrs['assignTarget'] !== 'agent' && attrs['assignTarget'] !== 'sentinel') {
    return { ...node, attrs: { ...attrs, assignTarget: 'agent' } };
  }
  return node;
}

/** The attrs the compiler reads off a send-email chip. */
export interface EmailChipData {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
}

export const emailChipNode = (): JSONContent => ({
  type: 'escalateChip',
  attrs: { escalationType: 'send-email', to: '', cc: '', bcc: '', subject: '', body: '' }
});

/** The attrs the compiler reads off a send-slack-message chip. */
export interface SlackChipData {
  slackChannel: string;
  slackMessage: string;
}

export const slackChipNode = (): JSONContent => ({
  type: 'escalateChip',
  attrs: { escalationType: 'send-slack-message', slackChannel: '', slackMessage: '' }
});

export const noteChipNode = (): JSONContent => ({
  type: 'escalateChip',
  attrs: { escalationType: 'send-private-note', noteContent: '' }
});

/** Reads a chip node back into what the UI needs. Returns null for anything else. */
export function readChip(node: JSONContent): ProtocolChip | null {
  const attrs = attrsOf(node);

  if (node.type === 'escalateChip') {
    const type = str(attrs['escalationType']);
    if (type === 'send-email') {
      return {
        kind: 'email',
        to: str(attrs['to']),
        cc: str(attrs['cc']),
        bcc: str(attrs['bcc']),
        subject: str(attrs['subject']),
        body: str(attrs['body'])
      };
    }
    if (type === 'send-private-note' || type === 'private-note') {
      return { kind: 'note', noteContent: str(attrs['noteContent']) };
    }
    if (type === 'send-slack-message') {
      return { kind: 'slack', slackChannel: str(attrs['slackChannel']), slackMessage: str(attrs['slackMessage']) };
    }
    if (type === 'unassign') return { kind: 'unassign', assignTarget: unassignTargetOf(attrs) };
    // `assign`, and the legacy `assign-to-human` (always an agent) it replaced.
    return {
      kind: 'assign',
      assignTarget: type === 'assign-to-human' ? 'agent' : assignTargetOf(attrs),
      agentId: str(attrs['agentId']),
      agentName: str(attrs['agentName']),
      agentEmail: str(attrs['agentEmail']),
      agentProvider: str(attrs['agentProvider']),
      sentinelUserId: str(attrs['sentinelUserId']),
      sentinelUserName: str(attrs['sentinelUserName']),
      sentinelUserEmail: str(attrs['sentinelUserEmail'])
    };
  }

  if (node.type === 'workflowLinkChip') {
    const workflowId = attrs['workflowId'];
    return { kind: 'workflowLink', workflowId: typeof workflowId === 'string' && workflowId ? workflowId : null, label: str(attrs['workflowName']) };
  }

  if (node.type !== 'actionChip') return null;

  const actionId = str(attrs['actionId']);
  const label = str(attrs['label']);

  if (actionId === SUB_PROTOCOL_ACTION_ID) {
    const subProtocolId = attrs['subProtocolId'];
    return { kind: 'subProtocol', subProtocolId: typeof subProtocolId === 'string' ? subProtocolId : null, label };
  }

  if (attrs['tool'] === FOLLOWUP_WAIT_TOOL && attrs['postProcessing'] === true) {
    const program = readWaitProgram(attrs['staticInputs']) ?? defaultWaitProgram();
    return { kind: 'wait', label, program };
  }

  if (attrs['postProcessing'] === true) {
    const action = postProcessingAction(attrs);
    if (action) return { kind: 'action', action, label };
  }

  if (actionId.startsWith(KIM_ACTION_PREFIX)) {
    const action = actionId.slice(KIM_ACTION_PREFIX.length) as TicketActionId;
    if (ACTION_CATALOG.some((entry) => entry.id === action)) return { kind: 'action', action, label };
  }

  const provider = str(attrs['provider']);
  const tool = str(attrs['tool']);
  if (isGoogleSheetTool(provider, tool)) {
    const sheet = { tool, sheetUrl: str(attrs['sheetUrl']) || null, tabName: str(attrs['tabName']) || null };
    return { kind: 'tool', actionId, label, provider, sheet };
  }
  return { kind: 'tool', actionId, label, provider };
}

/** The ticket action a chip counts as, for the Ticket Actions panel. */
export function chipActionId(chip: ProtocolChip): ProtocolActionId | null {
  switch (chip.kind) {
    case 'action':
      return chip.action;
    case 'email':
      return 'email';
    case 'note':
      return 'note';
    case 'slack':
      return 'slack';
    case 'assign':
      return 'assign';
    case 'unassign':
      return 'escalation-unassign';
    default:
      return null;
  }
}

/** Label the chip renders with: the tag or person's name where one was picked. */
export function chipLabel(chip: ProtocolChip): string {
  switch (chip.kind) {
    case 'tool':
      return chip.label;
    case 'assign': {
      // No one picked means the brand's escalation policy decides.
      const name =
        chip.assignTarget === 'sentinel' ? chip.sentinelUserName || chip.sentinelUserEmail : chip.agentName || chip.agentEmail;
      return `Assign to ${ASSIGN_TARGET_LABELS[chip.assignTarget]} : ${name || 'Brand policy'}`;
    }
    case 'unassign':
      return chip.assignTarget ? `Unassign ${ASSIGN_TARGET_LABELS[chip.assignTarget]}` : `Unassign : ${BRAND_DEFAULT_LABEL}`;
    case 'subProtocol':
      return chip.label ? `Run Sub-Protocol : ${chip.label}` : 'Run Sub-Protocol : Choose Sub-Protocol';
    case 'workflowLink':
      return chip.label ? `Linked Protocol : ${chip.label}` : 'Linked Protocol : Choose Protocol';
    case 'email':
      return actionMeta('email')?.menuLabel ?? 'Send Email';
    case 'note':
      return actionMeta('note')?.menuLabel ?? 'Send Private Note';
    case 'slack':
      return chip.slackChannel ? `Send Slack Message : ${chip.slackChannel}` : (actionMeta('slack')?.menuLabel ?? 'Send Slack Message');
    case 'wait':
      return waitChipLabel(chip.program);
    default:
      return chip.label;
  }
}
